import { SearchIcon } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import {
  Checkbox,
  FilterButton,
  FilterPillButton,
  FiltersDropdown,
  Pagination,
  Table,
  TextInput,
  useBreakpoint,
} from 'slab'
import { useGetMembersReimbursements, useGetMembersTransactions } from 'utils/web_api'
import { type BalanceTransaction, type GetMembersTransactionsCategoriesItem, type MemberReimbursement, type Pagination as PaginationType } from 'utils/web_api.schemas'

import { MobileReimbursementRow, ReimbursementRow } from './ReimbursementRow'
import { MobileTransactionRow, TransactionRow } from './TransactionRow'

interface TransactionsCardProps {
  planYear: number;
  perPage?: number;
}

const categories = [
  {
    key: 'employer_allowance' as GetMembersTransactionsCategoriesItem,
    label: 'Allowance',
  },
  {
    key: 'healthcare_expense' as GetMembersTransactionsCategoriesItem,
    label: 'Healthcare expense',
  },
  {
    key: 'insurance_premium' as GetMembersTransactionsCategoriesItem,
    label: 'Insurance',
  },
  {
    key: 'other' as GetMembersTransactionsCategoriesItem,
    label: 'Other',
  },
]

export const TransactionsCard: React.FC<TransactionsCardProps> = ({
  planYear,
  perPage = 20,
}) => {
  const params = new URLSearchParams(window.location.search)
  const { breakpointIsAtLeast } = useBreakpoint()
  const [currentPage, setCurrentPage] = useState(() => {
    return (params.get('page') || 1) as number
  })
  const [searchQuery, setSearchQuery] = useState(() => {
    return params.get('description') || ''
  })
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300)
  const [selectedCategories, setSelectedCategories] = useState<
    GetMembersTransactionsCategoriesItem[]>(() => {
    return (params.get('categories')?.split(',') || []) as GetMembersTransactionsCategoriesItem[]
  })

  const initialRender = useRef(true)
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false
      return
    }

    // if plan year changes after the initial render, we want to reset the categories & search query filter
    setSelectedCategories([])
    setSearchQuery('')
  }, [planYear])

  // Sync any changes back to the URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)

    const categoriesFromParams = urlParams.get('categories') || ''
    const descriptionFromParams = urlParams.get('description') || ''
    // Reset current page back to 1 if selected categories or the search query changed
    if (categoriesFromParams !== selectedCategories.join(',') || descriptionFromParams !== searchQuery) {
      setCurrentPage(1)
    }

    if (currentPage > 1) {
      urlParams.set('page', currentPage.toString())
    } else {
      urlParams.delete('page')
    }
    if (selectedCategories.length !== 0) {
      urlParams.set('categories', selectedCategories.join(','))
    } else {
      urlParams.delete('categories')
    }
    if (searchQuery) {
      urlParams.set('description', searchQuery)
    } else {
      urlParams.delete('description')
    }

    // Update URL without causing a page reload
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`
    window.history.replaceState({}, '', newUrl)
  }, [currentPage, selectedCategories, debouncedSearchQuery])

  const reimbursementPurposes = React.useMemo(() => {
    let result = []
    if (selectedCategories.includes('insurance_premium')) {
      result.push('premium')
    }
    if (selectedCategories.includes('healthcare_expense')) {
      result = result.concat(['medical', 'dental', 'vision', 'qme'])
    }
    return result
  }, [selectedCategories])

  // todo: We do not expect > 10 pending reimbursements if this happens, revisit pulling this into a separate card & table
  const { data: reimbursementsResponse, isLoading: isLoadingReimbursements } =
    useGetMembersReimbursements({
      page: 1,
      items: 10,
      ...(debouncedSearchQuery.trim() && {
        'filter[member_description]': debouncedSearchQuery.trim(),
      }),
      'filter[states][]': ['waiting_for_review', 'confirmed'],
      'filter[purposes][]': reimbursementPurposes,
      'filter[plan_year]': planYear,
    })

  const { data: transactionsResponse, isLoading: isLoadingTransactions } =
    useGetMembersTransactions({
      plan_year: planYear,
      'page[number]': currentPage,
      'page[size]': perPage,
      ...(debouncedSearchQuery.trim() && {
        description: debouncedSearchQuery.trim(),
      }),
      ...(selectedCategories.length > 0 && {
        'categories[]': selectedCategories,
      }),
    })

  const reimbursements: MemberReimbursement[] = reimbursementsResponse?.data ?? []
  const transactions: BalanceTransaction[] = transactionsResponse?.data ?? []
  const pagination: PaginationType = transactionsResponse?.pagination ?? {
    total_records: 0,
    current_page: 1,
    total_pages: 0,
    next_page: null,
    prev_page: null,
  }

  const handlePageChange = (pageIndex: number) => {
    // Convert from 0-based pageIndex to 1-based page[number]
    setCurrentPage(pageIndex + 1)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmedValue = e.currentTarget.value.trim()
      setSearchQuery(trimmedValue)
    }
  }

  const handleCategoryChange = (
    category: GetMembersTransactionsCategoriesItem,
    checked: boolean
  ) => {
    if (checked) {
      // Add category to selected categories
      setSelectedCategories((prev) => [...prev, category])
    } else {
      // Remove category from selected categories
      setSelectedCategories((prev) => prev.filter((c) => c !== category))
    }
  }

  const searchInput = (
    <TextInput
      placeholder='Search transactions'
      leadingIcon={SearchIcon}
      value={searchQuery}
      onChange={handleSearchChange}
      onKeyDown={handleSearchKeyDown}
    />
  )

  const categoryCheckboxes = (
    <>
      {categories.map((category) => (
        <Checkbox
          key={category.key}
          label={category.label}
          checked={selectedCategories.includes(category.key)}
          onChange={(e) => handleCategoryChange(category.key, e.target.checked)}
        />
      ))}
    </>
  )

  const paginationImpl = (
    <Pagination
      onPageChange={handlePageChange}
      pageIndex={pagination.current_page - 1}
      perPage={perPage}
      totalRows={pagination.total_records}
    />
  )

  const showTransactions = transactions.length > 0 || isLoadingTransactions

  const showReimbursements =
    (reimbursements.length > 0 || isLoadingReimbursements) &&
    (selectedCategories.length === 0 || reimbursementPurposes.length > 0)

  const showResults = showTransactions || showReimbursements
  const showEmptyState = !showResults

  if (breakpointIsAtLeast('lg')) {
    return (
      <div className='flex flex-col w-full bg-white rounded-2xl border border-gray-200 px-6 pt-6 pb-4'>
        <div className='flex flex-row w-full items-center gap-3 mb-6'>
          <div className='flex-grow max-w-sm'>{searchInput}</div>
          <div className='flex flex-row gap-3'>
            <FiltersDropdown
              trigger={
                <FilterPillButton
                  count={selectedCategories.length}
                  label='Category'
                />
              }
            >
              <FiltersDropdown.Filters>
                {categoryCheckboxes}
              </FiltersDropdown.Filters>
            </FiltersDropdown>
          </div>
        </div>
        {showEmptyState && (
          <div className='w-full text-center py-10 text-gray-500 body-bold'>
            No results found
          </div>
        )}
        {showResults && (
          <>
            <div className='w-full'>
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.ColumnHeader>Date</Table.ColumnHeader>
                    <Table.ColumnHeader>Description</Table.ColumnHeader>
                    <Table.ColumnHeader>Category</Table.ColumnHeader>
                    <Table.ColumnHeader align='right'>
                      Amount
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {showReimbursements && reimbursements.map(reimbursement => (
                    <ReimbursementRow key={reimbursement.id} reimbursement={reimbursement} />
                  ))}
                  {transactions.map((transaction) => (
                    <TransactionRow key={transaction.id} transaction={transaction} />
                  ))}
                </Table.Body>
              </Table>
            </div>
            <div className='flex justify-end mt-4'>{paginationImpl}</div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className='flex flex-col w-full bg-white rounded-2xl border border-gray-200 p-4 gap-4'>
      <div className='flex flex-row w-full items-center gap-3'>
        <div className='flex-grow'>{searchInput}</div>
        <FiltersDropdown
          trigger={
            <FilterButton
              filtersCount={selectedCategories.length}
              size='large'
            />
          }
        >
          <FiltersDropdown.Filters>
            <span className='body-bold text-main mb-2'>Category</span>
            {categoryCheckboxes}
          </FiltersDropdown.Filters>
        </FiltersDropdown>
      </div>
      {showEmptyState && (
        <div className='w-full text-center py-10 text-gray-500 body-bold'>
          No results found
        </div>
      )}
      {showResults && (
        <>
          <div className='w-full'>
            {showReimbursements && reimbursements.map(reimbursement => (
              <MobileReimbursementRow key={reimbursement.id} reimbursement={reimbursement} />
            ))}
            {transactions.map((transaction) => (
              <MobileTransactionRow key={transaction.id} transaction={transaction} />
            ))}
          </div>
          <div className='flex justify-end pt-4 border-t border-gray-200'>
            {paginationImpl}
          </div>
        </>
      )}
    </div>
  )
}

export default TransactionsCard
